import React, { useRef } from 'react'
import clsx from 'clsx'
import i18n from '@pureartisan/simple-i18n'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import { IconButton, SvgIcon } from '@material-ui/core'
import shallow from 'zustand/shallow'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import TuneIcon from '@material-ui/icons/TuneOutlined'
import LayersOutlined from '@material-ui/icons/LayersOutlined'
import BarChartIcon from '@material-ui/icons/BarChart'
import useStore from './../store'
import { DesktopUnifiedShareBtn } from '../Share'
import NewBadgeIcon from '../../../assets/Icons/NewBadgeIcon'

// Styles for this component.
const useStyles = makeStyles(theme => {
  return {
    root: ({ activeView, isMobile, breakpoint }) => {
      const hideControlPanel =
        activeView === 'embed' ||
        !!isMobile ||
        breakpoint === 'xs'
      return {
        display: hideControlPanel ? 'none' : 'flex',
        zIndex: theme.extras.controlPanel.zIndex,
        backgroundColor:
          theme.extras.variables.colors.darkSurface,
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${theme.extras.controlPanel.width}px`,
        // Adjust for different app bar height.
        height: `calc(100vh - ${theme.mixins.toolbar['@media (min-width:0px) and (orientation: landscape)'].minHeight}px)`,
        top: `${theme.mixins.toolbar['@media (min-width:0px) and (orientation: landscape)'].minHeight}px`,
        [theme.breakpoints.up('sm')]: {
          height: `calc(100vh - ${theme.mixins.toolbar['@media (min-width:600px)'].minHeight}px)`,
          top: `${theme.mixins.toolbar['@media (min-width:600px)'].minHeight}px`,
        },
        boxShadow: theme.shadows[3],
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }
    },
    button: {
      color: 'inherit',
      '&:hover': {
        backgroundColor: 'transparent',
        color: '#DAF0FF',
      },
      marginLeft: 'auto',
      marginRight: 'auto',
      '&.Mui-disabled': {
        color: theme.extras.variables.colors.lightGray,
      },
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    buttonContainer: {
      transition:
        'background-color 300ms ease-in-out, color 300ms ease-in-out',
      boxSizing: 'border-box',
      width: '100%',
      color: '#fff',
      textAlign: 'center',
      borderRight: `3px solid ${theme.extras.variables.colors.darkSurface}`,
      '&.active, &:focus-within': {
        color: theme.extras.variables.colors.darkSurface,
        backgroundColor: '#DAF0FF',
        // borderRight: `3px solid ${theme.extras.variables.colors.accentBackground}`,
        '& button, .MuiIconButton-label': {
          color: theme.extras.variables.colors.darkSurface,
        },
      },
      '&.active': {
        borderRight: `3px solid ${theme.extras.variables.colors.accentBackground}`,
      },
    },
    buttonLabel: {
      fontSize: '10px',
      letterSpacing: '1.5px',
    },
    buttonGroup: {
      paddingTop: '10vh',
    },
  }
})

const ControlPanel = ({ ...props }) => {
  // Header is not displayed if the view type is 'embed'
  const {
    slideoutFeatureId,
    activeView,
    slideoutPanel,
    setStoreValues,
    isMobile,
    breakpoint,
    // showSharePopover,
  } = useStore(
    state => ({
      slideoutFeatureId: state.slideoutFeatureId,
      activeView: state.activeView,
      slideoutPanel: state.slideoutPanel,
      setStoreValues: state.setStoreValues,
      isMobile: state.isMobile,
      breakpoint: state.breakpoint,
      // showSharePopover: state.showSharePopover,
    }),
    shallow,
  )

  const toggleSlideout = (val, e) => {
    if (
      slideoutPanel.active &&
      slideoutPanel.panel === val
    ) {
      setStoreValues({
        slideoutPanel: {
          ...slideoutPanel,
          active: false,
        },
        showSharePopover: false,
      })
      setTimeout(() => {
        // TODO: focus-within sticks when you click from eg chart to data controls
        // console.log('remove focus: ', val, refs[val])
        // blur to remove focus from button after click
        refs[val] &&
          refs[val].current &&
          refs[val].current.blur()
      }, 100)
    } else if (
      slideoutPanel.active &&
      slideoutPanel.panel != val
    ) {
      setStoreValues({
        slideoutPanel: {
          ...slideoutPanel,
          panel: val,
        },
        showSharePopover: false,
      })
    } else {
      setStoreValues({
        slideoutPanel: {
          ...slideoutPanel,
          active: true,
          panel: val,
        },
        showSharePopover: false,
      })
    }
  }

  const classes = useStyles({
    activeView,
    isMobile,
    breakpoint,
  })

  const refs = {
    control: useRef(),
    overlays: useRef(),
    chart: useRef(),
    location: useRef(),
    faq: useRef(),
  }
  return (
    <Box className={clsx('control-panel', classes.root)}>
      <div className={classes.buttonGroup}>
        <div
          className={clsx(
            classes.buttonContainer,
            slideoutPanel.active &&
              slideoutPanel.panel === 'control'
              ? 'active'
              : '',
          )}
        >
          <IconButton
            onClick={e => {
              toggleSlideout('control', e)
            }}
            className={clsx(
              'control-panel-button',
              'user-hints-target-norming',
              classes.button,
            )}
            disableRipple={true}
            ref={refs.control}
          >
            <div>
              <TuneIcon fontSize={'large'} />
              <div
                className={classes.buttonLabel}
                dangerouslySetInnerHTML={{
                  __html: i18n.translate(
                    'CONTROL_PANEL_CONTROL',
                  ),
                }}
              ></div>
            </div>
          </IconButton>
        </div>
        <div
          className={clsx(
            classes.buttonContainer,
            slideoutPanel.active &&
              slideoutPanel.panel === 'overlays'
              ? 'active'
              : '',
          )}
        >
          <IconButton
            onClick={e => {
              toggleSlideout('overlays', e)
            }}
            className={clsx(
              'control-panel-button',
              classes.button,
            )}
            disableRipple={true}
            ref={refs.overlays}
          >
            <div>
              <LayersOutlined fontSize={'large'} />
              {/* <NewBadgeIcon
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 12,
                }}
              /> */}
              <div
                className={classes.buttonLabel}
                dangerouslySetInnerHTML={{
                  __html: i18n.translate(
                    'CONTROL_PANEL_OVERLAYS',
                  ),
                }}
              ></div>
            </div>
          </IconButton>
        </div>
        <div
          className={clsx(
            classes.buttonContainer,
            slideoutPanel.active &&
              slideoutPanel.panel === 'chart'
              ? 'active'
              : '',
          )}
        >
          <IconButton
            onClick={e => {
              toggleSlideout('chart', e)
            }}
            ref={refs.chart}
            className={clsx(
              'control-panel-button',
              classes.button,
            )}
            disableRipple={true}
          >
            <div>
              <BarChartIcon fontSize={'large'} />
              <div
                className={classes.buttonLabel}
                dangerouslySetInnerHTML={{
                  __html: i18n.translate(
                    'CONTROL_PANEL_CHART',
                  ),
                }}
              ></div>
            </div>
          </IconButton>
        </div>
        <div
          className={clsx(
            classes.buttonContainer,
            slideoutPanel.active &&
              slideoutPanel.panel === 'location'
              ? 'active'
              : '',
          )}
        >
          <IconButton
            onClick={e => {
              toggleSlideout('location', e)
            }}
            ref={refs.location}
            className={clsx(
              'control-panel-button',
              classes.button,
            )}
            disabled={!slideoutFeatureId}
            disableRipple={true}
          >
            <div>
              <RoomOutlinedIcon fontSize={'large'} />
              <div
                className={classes.buttonLabel}
                dangerouslySetInnerHTML={{
                  __html: i18n.translate(
                    'CONTROL_PANEL_LOCATION',
                  ),
                }}
              ></div>
            </div>
          </IconButton>
        </div>
        <div
          className={clsx(
            classes.buttonContainer,
            slideoutPanel.active &&
              slideoutPanel.panel === 'faq'
              ? 'active'
              : '',
          )}
        >
          <IconButton
            onClick={e => {
              toggleSlideout('faq', e)
            }}
            ref={refs.faq}
            className={clsx(
              'control-panel-button',
              classes.button,
            )}
            disableRipple={true}
          >
            <div>
              <HelpOutlineIcon fontSize={'large'} />
              <div
                className={classes.buttonLabel}
                dangerouslySetInnerHTML={{
                  __html: i18n.translate(
                    'CONTROL_PANEL_FAQS',
                  ),
                }}
              ></div>
            </div>
          </IconButton>
        </div>
        <DesktopUnifiedShareBtn
          className={clsx(
            classes.buttonContainer,
            slideoutPanel.active &&
              slideoutPanel.panel === 'share'
              ? 'active'
              : '',
          )}
        >
          <div
            className={classes.buttonLabel}
            dangerouslySetInnerHTML={{
              __html: i18n.translate('CONTROL_PANEL_SHARE'),
            }}
          ></div>
        </DesktopUnifiedShareBtn>
      </div>
    </Box>
  )
}

ControlPanel.propTypes = {}

ControlPanel.defaultProps = {}

export default ControlPanel
